import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, getCurrentToken, API_BASE } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { FundraisingPills, FundraisingActivityPill } from '../components/FundraisingPills';
import { HowToDocumentsModal } from '../components/organizer/HowToDocumentsModal';
import { useDownloadAudit } from '../hooks/useDownloadAudit';
import { DownloadAuditBanner } from '../components/DownloadAuditBanner';

interface DashboardMetrics {
  totalAttendees: number;
  totalDelegates: number;
  totalMembers?: number;
  eventsCount?: number;
  registrationsPerEvent?: Array<{ eventId: string; eventName: string; count: number }>;
  overdueTaskCount: number;
  tasksByStatus: { ToDo: number; InProgress: number; Done: number; toDo?: number; inProgress?: number; done?: number };
  budgetVariance: number;
  ticketsSold: number;
  ticketRevenue: number;
  totalRaised: number;
  fundraisingGoal: number;
  fundraisingActivities: FundraisingActivityPill[];
}

// Demo data for when the backend is not available
const DEMO_METRICS: DashboardMetrics = {
  totalAttendees: 247,
  totalDelegates: 38,
  eventsCount: 12,
  overdueTaskCount: 3,
  tasksByStatus: { ToDo: 8, InProgress: 13, Done: 42 },
  budgetVariance: 2450,
  ticketsSold: 189,
  ticketRevenue: 18900,
  totalRaised: 45230,
  fundraisingGoal: 75000,
  fundraisingActivities: [
    { id: 'demo-1', name: 'Spring 5K Walk/Run', type: 'VirtualWalkRun', raised: 18500, goalAmount: 25000 },
    { id: 'demo-2', name: 'KDU T-Shirt Fundraiser', type: 'TshirtSale', raised: 8730, goalAmount: 10000 },
    { id: 'demo-3', name: 'Corporate Sponsorships', type: 'Sponsorship', raised: 12000, goalAmount: 20000 },
    { id: 'demo-4', name: 'General Donations', type: 'Donation', raised: 6000, goalAmount: 20000 },
  ],
};

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.85rem', marginBottom: '1.5rem' },
  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' },
  metricCard: { padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  metricValue: { fontSize: '1.75rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' },
  metricLabel: { fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.75rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.75rem' },
  progressBar: { width: '100%', height: '20px', backgroundColor: '#e0e0e0', borderRadius: '10px', overflow: 'hidden', marginTop: '0.5rem' },
  progressFill: { height: '100%', borderRadius: '10px', transition: 'width 0.3s' },
  taskStatusBar: { display: 'flex', gap: '1rem', marginTop: '0.5rem' },
  taskStatusItem: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' },
  dot: { width: '12px', height: '12px', borderRadius: '50%' },
  quickActions: { display: 'flex', flexWrap: 'wrap' as const, gap: '0.75rem', marginTop: '1rem' },
};

export function DashboardPage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const isDemoMode = accessToken === 'demo-token';
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(isDemoMode ? DEMO_METRICS : null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(isDemoMode ? new Date() : null);
  const [error, setError] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showTasksTable, setShowTasksTable] = useState(false);
  const [tasksList, setTasksList] = useState<{ id: string; title: string; status: string; dueDate: string; isOverdue: boolean; subcommittee?: { name: string } }[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showHowToDocs, setShowHowToDocs] = useState(false);
  const { auditedDownload, bannerState, dismissBanner } = useDownloadAudit();

  async function fetchMetrics() {
    if (isDemoMode) {
      setMetrics(DEMO_METRICS);
      setLastUpdated(new Date());
      return;
    }
    try {
      const data = await apiClient<DashboardMetrics>('/dashboard/metrics');
      setMetrics(data);
      setLastUpdated(new Date());
      setError('');
    } catch {
      setMetrics(DEMO_METRICS);
      setLastUpdated(new Date());
      setError('');
    }
  }

  useEffect(() => {
    fetchMetrics();
    // Auto-refresh every 30 seconds (polling fallback)
    intervalRef.current = setInterval(fetchMetrics, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  async function handleExportRoster() {
    await auditedDownload('Export Attendee Roster', 'attendee-roster.csv', async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/dashboard/export/roster`,
          { headers: { Authorization: `Bearer ${document.cookie}` } }
        );
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'attendee-roster.csv'; a.click();
        URL.revokeObjectURL(url);
      } catch {
        setError('Failed to export roster.');
      }
    });
  }

  async function handleDownloadOfflineLookup() {
    const fileName = `kdu-legacy-ball-offline-lookup-${new Date().toISOString().split('T')[0]}.html`;
    await auditedDownload('Download Offline Guest Lookup', fileName, async () => {
      try {
        const token = getCurrentToken();
        const response = await fetch(
          `${API_BASE}/table-management/offline-snapshot`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        setError('Failed to download offline lookup.');
      }
    });
  }

  const fundraisingPct = metrics && metrics.fundraisingGoal > 0
    ? Math.min((metrics.totalRaised / metrics.fundraisingGoal) * 100, 100)
    : 0;

  const totalTasks = metrics
    ? (metrics.tasksByStatus.ToDo ?? metrics.tasksByStatus.toDo ?? 0) + (metrics.tasksByStatus.InProgress ?? metrics.tasksByStatus.inProgress ?? 0) + (metrics.tasksByStatus.Done ?? metrics.tasksByStatus.done ?? 0)
    : 0;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Dashboard</h1>
      <p style={styles.subtitle}>
        {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()} · Auto-refreshes every 30s` : 'Loading...'}
      </p>
      {error && <div style={{ color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}

      {metrics && (
        <>
          {/* Metric Cards */}
          <div style={styles.metricsGrid}>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/registrations')}>
              <div style={styles.metricValue}>{metrics.totalAttendees}</div>
              <div style={styles.metricLabel}>Legacy Ball Attendees</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/chapter-members-attending')}>
              <div style={styles.metricValue}>{metrics.totalMembers ?? metrics.totalDelegates}</div>
              <div style={styles.metricLabel}>KDU GA Members Attending Legacy Ball</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/events')}>
              <div style={styles.metricValue}>{metrics.eventsCount ?? metrics.registrationsPerEvent?.length ?? 0}</div>
              <div style={styles.metricLabel}>Events</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/tasks')}>
              <div style={{ ...styles.metricValue, color: metrics.overdueTaskCount > 0 ? '#d32f2f' : '#1a6b3c' }}>
                {metrics.overdueTaskCount}
              </div>
              <div style={styles.metricLabel}>Overdue Tasks</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/legacy-ball')}>
              <div style={styles.metricValue}>{metrics.ticketsSold}</div>
              <div style={styles.metricLabel}>Tickets Sold</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/legacy-ball')}>
              <div style={styles.metricValue}>${metrics.ticketRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <div style={styles.metricLabel}>Ticket Revenue</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/budget')}>
              <div style={{ ...styles.metricValue, color: metrics.budgetVariance < 0 ? '#d32f2f' : '#1a6b3c' }}>
                {metrics.budgetVariance < 0 ? '-' : '+'}${Math.abs(metrics.budgetVariance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <div style={styles.metricLabel}>Budget Variance</div>
            </div>
            <div style={{ ...styles.metricCard, cursor: 'pointer' }} onClick={() => navigate('/fundraising')}>
              <div style={styles.metricValue}>
                ${metrics.totalRaised.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <div style={styles.metricLabel}>
                Raised / ${metrics.fundraisingGoal.toLocaleString('en-US', { minimumFractionDigits: 2 })} Goal
              </div>
            </div>
          </div>

          {/* Tasks by Status */}
          <div style={{ ...styles.section, cursor: 'pointer' }} onClick={async () => {
            if (showTasksTable) {
              setShowTasksTable(false);
              return;
            }
            setTasksLoading(true);
            try {
              const tasks = await apiClient<{ id: string; title: string; status: string; dueDate: string; isOverdue: boolean; subcommittee?: { name: string } }[]>('/tasks');
              setTasksList(tasks);
            } catch {
              setTasksList([]);
            } finally {
              setTasksLoading(false);
              setShowTasksTable(true);
            }
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Tasks Overview</h2>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>{showTasksTable ? '▲ Hide details' : '▼ Click to view all tasks'}</span>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <div style={styles.taskStatusBar}>
                <div style={styles.taskStatusItem}>
                  <span style={{ ...styles.dot, backgroundColor: '#666' }} />
                  To Do: {metrics.tasksByStatus.ToDo ?? metrics.tasksByStatus.toDo ?? 0}
                </div>
                <div style={styles.taskStatusItem}>
                  <span style={{ ...styles.dot, backgroundColor: '#c9a84c' }} />
                  In Progress: {metrics.tasksByStatus.InProgress ?? metrics.tasksByStatus.inProgress ?? 0}
                </div>
                <div style={styles.taskStatusItem}>
                  <span style={{ ...styles.dot, backgroundColor: '#1a6b3c' }} />
                  Done: {metrics.tasksByStatus.Done ?? metrics.tasksByStatus.done ?? 0}
                </div>
              </div>
              {totalTasks > 0 && (
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${((metrics.tasksByStatus.Done ?? metrics.tasksByStatus.done ?? 0) / totalTasks) * 100}%`, backgroundColor: '#1a6b3c' }} />
                </div>
              )}
            </div>

            {/* Expanded Tasks Table */}
            {showTasksTable && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }} onClick={(e) => e.stopPropagation()}>
                {tasksLoading ? (
                  <div style={{ textAlign: 'center' as const, padding: '1rem', color: '#666' }}>Loading tasks...</div>
                ) : tasksList.length === 0 ? (
                  <div style={{ textAlign: 'center' as const, padding: '1rem', color: '#666' }}>No tasks found.</div>
                ) : (
                  <div style={{ overflowX: 'auto' as const }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left' as const, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#374151' }}>Title</th>
                          <th style={{ textAlign: 'left' as const, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#374151' }}>Sub-Committee</th>
                          <th style={{ textAlign: 'left' as const, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#374151' }}>Status</th>
                          <th style={{ textAlign: 'left' as const, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#374151' }}>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasksList.map((task) => (
                          <tr key={task.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#333' }}>
                              {task.title}
                              {task.isOverdue && task.status !== 'Done' && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>⚠ OVERDUE</span>
                              )}
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#666' }}>{task.subcommittee?.name || '—'}</td>
                            <td style={{ padding: '0.5rem 0.75rem' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '3px',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: '#fff',
                                backgroundColor: task.status === 'Done' ? '#1a6b3c' : task.status === 'InProgress' ? '#c9a84c' : '#666',
                              }}>
                                {task.status === 'InProgress' ? 'In Progress' : task.status === 'ToDo' ? 'To Do' : task.status}
                              </span>
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#666' }}>{new Date(task.dueDate).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fundraising Progress */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Fundraising Progress</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '1.1rem' }}>
                <strong style={{ color: '#1a6b3c' }}>${metrics.totalRaised.toLocaleString()}</strong>
                <span style={{ color: '#666', fontSize: '0.9rem' }}> / ${metrics.fundraisingGoal.toLocaleString()} goal</span>
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: fundraisingPct >= 100 ? '#1a6b3c' : '#c9a84c' }}>
                {fundraisingPct.toFixed(0)}%
              </div>
            </div>

            {/* Stacked progress bar showing each activity's contribution */}
            <div style={{ width: '100%', height: '28px', backgroundColor: '#f0f0f0', borderRadius: '14px', overflow: 'hidden', display: 'flex', position: 'relative' as const }}>
              {metrics.fundraisingActivities.map((activity, i) => {
                const activityPct = metrics.fundraisingGoal > 0
                  ? (activity.raised / metrics.fundraisingGoal) * 100
                  : 0;
                const colors = ['#1a6b3c', '#fdd835', '#2196f3', '#e65100', '#7b1fa2', '#00838f'];
                return activityPct > 0 ? (
                  <div
                    key={activity.id}
                    title={`${activity.name}: $${activity.raised.toLocaleString()}`}
                    style={{
                      width: `${Math.min(activityPct, 100)}%`,
                      height: '100%',
                      backgroundColor: colors[i % colors.length],
                      transition: 'width 0.5s ease',
                      minWidth: activityPct > 0 ? '4px' : '0',
                    }}
                  />
                ) : null;
              })}
            </div>

            {/* Pie chart + Legend side by side */}
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', marginTop: '1.25rem', flexWrap: 'wrap' as const }}>
              {/* Pie Chart (CSS conic-gradient) */}
              {(() => {
                const colors = ['#1a6b3c', '#fdd835', '#2196f3', '#e65100', '#7b1fa2', '#00838f'];
                const total = metrics.totalRaised || 1;
                let cumulative = 0;
                const stops = metrics.fundraisingActivities.map((activity, i) => {
                  const start = cumulative;
                  const pct = (activity.raised / total) * 100;
                  cumulative += pct;
                  return `${colors[i % colors.length]} ${start}% ${cumulative}%`;
                });
                // Fill remaining with light gray if not 100%
                if (cumulative < 100) {
                  stops.push(`#e0e0e0 ${cumulative}% 100%`);
                }
                const gradient = `conic-gradient(${stops.join(', ')})`;
                return (
                  <div style={{ width: '140px', height: '140px', borderRadius: '50%', background: gradient, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }} title="Fundraising breakdown by activity" />
                );
              })()}

              {/* Legend */}
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.5rem', flex: 1 }}>
                {metrics.fundraisingActivities.map((activity, i) => {
                  const colors = ['#1a6b3c', '#fdd835', '#2196f3', '#e65100', '#7b1fa2', '#00838f'];
                  const sharePct = metrics.totalRaised > 0 ? Math.round((activity.raised / metrics.totalRaised) * 100) : 0;
                  const goalPct = activity.goalAmount > 0 ? Math.round((activity.raised / activity.goalAmount) * 100) : 0;
                  return (
                    <div key={activity.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: colors[i % colors.length], display: 'inline-block', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#333' }}>{activity.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          ${activity.raised.toLocaleString()} of ${activity.goalAmount.toLocaleString()} ({goalPct}%) · {sharePct}% of total
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {metrics.totalRaised >= metrics.fundraisingGoal && metrics.fundraisingGoal > 0 && (
              <div style={{ marginTop: '0.75rem', padding: '0.5rem 1rem', backgroundColor: '#e8f5e9', borderRadius: '4px', color: '#1a6b3c', fontWeight: 600 }}>
                🎉 Fundraising Goal Reached!
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Quick Actions</h2>
            <div style={styles.quickActions}>
              <button style={styles.btn} onClick={handleExportRoster}>Export Attendee Roster (CSV)</button>
              <button style={styles.btn} onClick={handleDownloadOfflineLookup}>Download Offline Guest Lookup</button>
              <button style={styles.btnGold} onClick={() => navigate('/events')}>Manage Events</button>
              <button style={styles.btnGold} onClick={() => navigate('/tasks')}>View Tasks</button>
              <button style={styles.btnGold} onClick={() => navigate('/budget')}>View Budget</button>
              <button style={styles.btnGold} onClick={() => navigate('/fundraising')}>Fundraising</button>
              <button style={styles.btn} onClick={() => setShowHowToDocs(true)}>KDUHUB How To Documents</button>
              <button style={styles.btn} onClick={() => window.open('/vendor-system-infographic.html', 'VendorSystemOverview', 'width=1100,height=800,scrollbars=yes,resizable=yes')}>Vendor System Overview</button>
            </div>
          </div>
        </>
      )}

      <HowToDocumentsModal open={showHowToDocs} onClose={() => setShowHowToDocs(false)} />
      <DownloadAuditBanner showing={bannerState.showing} timestamp={bannerState.timestamp} action={bannerState.action} onDismiss={dismissBanner} />
    </div>
  );
}
